import { apiClient } from '@/lib/api';

/**
 * Generic, app-wide audit/incident log — any feature can write an entry here without a new
 * endpoint per event kind. `eventType` is free-form and caller-owned (e.g. "device.crc_mismatch").
 * Distinct from the harness Communication Log: that captures full per-frame detail scoped to an
 * active Batch; this is a lightweight, always-available incident trail independent of any Batch.
 */
export type AuditLogSeverity = 'Info' | 'Warning' | 'Error' | 'Critical';

export interface CreateAuditLogRequest {
  eventType: string;
  severity: AuditLogSeverity;
  message: string;
  details?: unknown;
  batchId?: string | null;
  deviceUid?: string | null;
}

export interface AuditLogDto {
  id: string;
  eventType: string;
  severity: AuditLogSeverity;
  message: string;
  details: unknown | null;
  userId: string;
  batchId: string | null;
  deviceUid: string | null;
  createdAt: string;
}

export interface ListAuditLogParams {
  eventType?: string;
  severity?: AuditLogSeverity;
  batchId?: string;
  page?: number;
  pageSize?: number;
}

export interface PagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export const auditLogService = {
  /**
   * Records one audit-log entry attributed to the current authenticated user.
   * POST /api/v1/audit-log
   */
  async log(request: CreateAuditLogRequest): Promise<AuditLogDto> {
    const response = await apiClient.post<AuditLogDto>('/api/v1/audit-log', request);
    return response.data;
  },

  /**
   * Lists audit-log entries newest first, optionally filtered.
   * GET /api/v1/audit-log
   */
  async list(params: ListAuditLogParams = {}): Promise<PagedResult<AuditLogDto>> {
    const response = await apiClient.get<PagedResult<AuditLogDto>>('/api/v1/audit-log', { params });
    return response.data;
  },
};
